import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useSearchParams } from 'react-router-dom';
import { useRequestPasswordResetMutation } from '@/generated/graphql';
import { AppNav } from '@/components/AppNav';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from '@/components/ui/card';
import { CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Footer } from '@/components/Footer';

export function ForgotPassword() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');

  return (
    <div className="flex min-h-screen flex-col">
      <AppNav />
      <div
        className={`relative z-[2] mx-auto flex max-w-screen-md flex-1 flex-col items-center justify-center`}
      >
        <ForgotPasswordForm initialEmailValue={email || ''} />
      </div>
    </div>
  );
}

function ForgotPasswordForm({
  initialEmailValue,
}: {
  initialEmailValue: string;
}) {
  const [email, setEmail] = useState(initialEmailValue);

  const [requestPasswordReset, { loading }] = useRequestPasswordResetMutation();

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    // Prevent submission if email is invalid
    if (!isValidEmail) {
      return;
    }

    try {
      await requestPasswordReset({ variables: { email } });
      toast.success(
        'If an account exists with that email, a password reset link has been sent!',
      );
      setEmail(''); // Reset email field on success
    } catch (error: any) {
      toast.error(
        error.graphQLErrors?.[0]?.message ||
          'Failed to send password reset link',
      );
    }
  };

  const isValidEmail = email && email.includes('@');

  return (
    <div className="flex flex-col gap-6">
      <Card className="min-w-[300px]">
        <CardHeader>
          <CardTitle className="flex items-center justify-start">
            <span>Forgot Password</span>
          </CardTitle>
          <CardDescription>
            Enter your email to receive a password reset link.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-2">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
            />
            <div className="flex justify-start gap-2">
              <Button type="submit" disabled={!isValidEmail || loading}>
                {loading ? 'Sending Reset Link...' : 'Send Reset Link'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
      <Footer />
    </div>
  );
}
